/**
 * SCR-207: Secure Document Upload Modal
 * BRD-014: Client-Side Document Redaction | US-207: Upload with PII Protection
 *
 * A secure document upload modal that:
 * 1. Scans documents for PII before upload
 * 2. Allows user to review and redact detected PII
 * 3. Only uploads the redacted version
 *
 * This is the recommended upload modal for privacy-conscious users.
 * It wraps the scan → preview → redact → upload flow into a single component.
 */

import { useState, useCallback, useEffect } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import {
  Modal,
  Button,
  Icon,
  useToast,
} from '@myvault/shared';
import { PIIScanModal } from './PIIScanModal';
import { RedactionPreviewModal } from './RedactionPreviewModal';
import { RedactionUploadModal } from './RedactionUploadModal';
import { useUploadDocuments } from '../hooks/useDocuments';
import type { ScanResult } from '../hooks/usePIIScan';
import type { RedactionMetadata } from '../hooks/useUploadWithRedaction';

// Constants
const ACCEPTED_TYPES = {
  'application/pdf': ['.pdf'],
};
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

type UploadStep = 'select' | 'scanning' | 'preview' | 'upload' | 'done';

interface SecureDocumentUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploadComplete?: (documentIds: string[]) => void;
  folderId?: string;
}

export function SecureDocumentUploadModal({
  open,
  onOpenChange,
  onUploadComplete,
  folderId,
}: SecureDocumentUploadModalProps) {
  const [step, setStep] = useState<UploadStep>('select');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [redactedBlob, setRedactedBlob] = useState<Blob | null>(null);
  const [redactionMetadata, setRedactionMetadata] = useState<RedactionMetadata | null>(null);
  const { error: showError } = useToast();
  const uploadMutation = useUploadDocuments();

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep('select');
      setSelectedFile(null);
      setScanResult(null);
      setRedactedBlob(null);
      setRedactionMetadata(null);
    }
  }, [open]);

  // Handle file selection
  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      // Handle rejected files
      rejectedFiles.forEach((rejection) => {
        const errors = rejection.errors
          .map((e) => {
            if (e.code === 'file-too-large') return 'Bestand is te groot (max 25MB)';
            if (e.code === 'file-invalid-type') return 'Alleen PDF bestanden worden ondersteund';
            return e.message;
          })
          .join(', ');
        showError('Bestand afgewezen', `${rejection.file.name}: ${errors}`);
      });

      if (acceptedFiles.length > 0) {
        // Only handle first file for now (single-file secure upload)
        setSelectedFile(acceptedFiles[0]);
        setStep('scanning');
      }
    },
    [showError]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: false,
    disabled: step !== 'select',
  });

  // Handle PII scan result
  const handlePIIFound = (result: ScanResult) => {
    setScanResult(result);
    setStep('preview');
  };

  // Handle no PII found - proceed to regular upload
  const handleNoPII = async (file: File) => {
    try {
      const result = await uploadMutation.mutateAsync({
        files: [file],
        folderId,
      });
      setStep('done');
      setTimeout(() => {
        onUploadComplete?.(result.map((d) => d.id));
        onOpenChange(false);
      }, 1500);
    } catch {
      setStep('select');
    }
  };

  // Handle redact and upload
  const handleRedactAndUpload = (blob: Blob, metadata: RedactionMetadata) => {
    setRedactedBlob(blob);
    setRedactionMetadata(metadata);
    setStep('upload');
  };

  // Handle upload without redaction
  const handleUploadWithoutRedaction = async (file: File) => {
    try {
      const result = await uploadMutation.mutateAsync({
        files: [file],
        folderId,
      });
      setStep('done');
      setTimeout(() => {
        onUploadComplete?.(result.map((d) => d.id));
        onOpenChange(false);
      }, 1500);
    } catch {
      setStep('select');
    }
  };

  // Handle successful redacted upload
  const handleRedactedUploadComplete = (documentId: string) => {
    setStep('done');
    setTimeout(() => {
      onUploadComplete?.([documentId]);
      onOpenChange(false);
    }, 1000);
  };

  // Handle cancel at any step
  const handleCancel = () => {
    setStep('select');
    setSelectedFile(null);
    setScanResult(null);
    setRedactedBlob(null);
    setRedactionMetadata(null);
  };

  const handleClose = () => {
    if (step === 'select' || step === 'done') {
      onOpenChange(false);
    }
  };

  return (
    <>
      {/* Main selection modal */}
      <Modal
        open={open && step === 'select'}
        onOpenChange={(isOpen) => {
          if (!isOpen) handleClose();
        }}
        title="Document veilig uploaden"
        size="md"
      >
        <div className="space-y-6">
          {/* Privacy info banner */}
          <div className="flex items-start gap-3 p-4 bg-mv-blue-50 border border-mv-blue-200 rounded-lg">
            <Icon name="shieldCheck" className="w-6 h-6 text-mv-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-mv-blue-900">Veilige upload met privacy-check</p>
              <p className="text-sm text-mv-blue-700 mt-1">
                Uw document wordt automatisch gescand op gevoelige gegevens (BSN, IBAN, etc.)
                voordat het wordt geüpload. U krijgt de kans om deze gegevens te verbergen.
              </p>
            </div>
          </div>

          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
              transition-colors duration-200
              ${isDragActive ? 'border-mv-blue-600 bg-mv-blue-50' : 'border-mv-gray-300 hover:border-mv-blue-400'}
            `}
            aria-label="Document upload zone. Sleep een PDF hierheen of klik om te bladeren."
          >
            <input {...getInputProps()} />
            <Icon name="cloudArrowUp" className="w-12 h-12 mx-auto text-mv-gray-400 mb-4" />
            {isDragActive ? (
              <p className="text-lg font-medium text-mv-blue-600">Laat document los om te scannen</p>
            ) : (
              <>
                <p className="text-lg font-medium text-mv-gray-700">Sleep uw document hierheen</p>
                <p className="text-sm text-mv-gray-500 mt-1">of klik om te bladeren</p>
              </>
            )}
            <p className="text-xs text-mv-gray-400 mt-4">Alleen PDF • Max 25MB</p>
          </div>

          {/* How it works */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-mv-gray-700">Hoe werkt het?</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-mv-blue-100 flex items-center justify-center">
                  <Icon name="magnifyingGlass" size="sm" className="text-mv-blue-600" />
                </div>
                <p className="text-xs text-mv-gray-600">1. Scannen</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-mv-amber-100 flex items-center justify-center">
                  <Icon name="eyeSlash" size="sm" className="text-mv-amber-600" />
                </div>
                <p className="text-xs text-mv-gray-600">2. Verbergen</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-mv-green-100 flex items-center justify-center">
                  <Icon name="cloudArrowUp" size="sm" className="text-mv-green-600" />
                </div>
                <p className="text-xs text-mv-gray-600">3. Uploaden</p>
              </div>
            </div>
          </div>

          {/* Cancel button */}
          <div className="flex justify-end pt-4 border-t border-mv-gray-200">
            <Button variant="secondary" onClick={handleClose}>
              Annuleren
            </Button>
          </div>
        </div>
      </Modal>

      {/* Done confirmation */}
      <Modal
        open={open && step === 'done'}
        onOpenChange={(isOpen) => {
          if (!isOpen) handleClose();
        }}
        title="Upload voltooid"
        size="sm"
      >
        <div className="space-y-6 text-center py-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-mv-green-100 flex items-center justify-center">
            <Icon name="checkCircle" className="w-10 h-10 text-mv-green-600" />
          </div>
          <div>
            <p className="text-lg font-medium text-mv-gray-900">Document succesvol geüpload</p>
            {redactionMetadata && (
              <p className="text-sm text-mv-gray-500 mt-1">
                {redactionMetadata.redactions.length} gevoelige gegevens verborgen
              </p>
            )}
          </div>
        </div>
      </Modal>

      {/* PII Scan Modal */}
      <PIIScanModal
        open={open && step === 'scanning'}
        onOpenChange={() => {}}
        file={selectedFile}
        onPIIFound={handlePIIFound}
        onNoPII={handleNoPII}
        onCancel={handleCancel}
      />

      {/* Redaction Preview Modal */}
      <RedactionPreviewModal
        open={open && step === 'preview'}
        onOpenChange={() => {}}
        scanResult={scanResult}
        onRedactAndUpload={handleRedactAndUpload}
        onUploadWithoutRedaction={handleUploadWithoutRedaction}
        onCancel={handleCancel}
      />

      {/* Redaction Upload Modal */}
      {redactedBlob && redactionMetadata && selectedFile && (
        <RedactionUploadModal
          open={open && step === 'upload'}
          onOpenChange={() => {}}
          redactedFile={redactedBlob}
          originalFilename={selectedFile.name}
          redactionMetadata={redactionMetadata}
          onUploadComplete={handleRedactedUploadComplete}
          onCancel={handleCancel}
        />
      )}
    </>
  );
}

export default SecureDocumentUploadModal;
